import { useCallback, useEffect, useRef, useState } from "react"

import { endAvatarGesture, startAvatarGesture } from "src/utils/avatarGestureGuard"
import { useWebLayoutConstraints } from "./useWebLayoutConstraints"

/**
 * Set this to `true` to enable verbose `[avatar-drag]` traces in the browser
 * console for the web pointer-event drag pipeline. Pair with the matching
 * flags in `avatarGestureGuard.ts` and `AvatarOverlay.tsx` to get full
 * coverage when reproducing a regression.
 */
const DEBUG_AVATAR_DRAG = false

function dlog(...args: unknown[]) {
  if (!DEBUG_AVATAR_DRAG) return
  // eslint-disable-next-line no-console
  console.log("[avatar-drag]", ...args)
}

type SnapCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right"

interface UseWebAvatarDragProps {
  snapCorner: SnapCorner
  setSnapCorner: (corner: SnapCorner) => void
  minimizedWidth: number
  minimizedHeight: number
  topClearance?: number
  isEnabled: boolean
  onExpand: () => void
  onDismissToTab: () => void
}

interface DragPosition {
  x: number
  y: number
}

const MINIMIZED_MARGIN = 16
const TOP_PADDING = 16
const BOTTOM_PADDING = 100 // Extra padding from bottom for captions

/**
 * Hook that manages web-specific avatar dragging behavior for minimized mode.
 *
 * Uses Pointer Events (which unify mouse, touch, and pen input) instead of
 * Reanimated gestures, which don't drive `webPosition` on web. Pointer events
 * are required for mobile browsers and Chrome's mobile emulation, where bare
 * mouse events are not dispatched for touch input.
 */
export function useWebAvatarDrag({
  snapCorner,
  setSnapCorner,
  minimizedWidth,
  minimizedHeight,
  topClearance = 0,
  isEnabled,
  onExpand,
  onDismissToTab,
}: UseWebAvatarDragProps) {
  const layout = useWebLayoutConstraints()
  const [webPosition, setWebPosition] = useState<DragPosition | null>(null)
  const isDraggingRef = useRef(false)
  const dragStartPos = useRef({ x: 0, y: 0 })
  const dragStartAvatarPos = useRef({ x: 0, y: 0 })
  const dragStartTime = useRef(0)
  const hasMovedRef = useRef(false)
  const activePointerIdRef = useRef<number | null>(null)
  const clickSuppressorCleanupRef = useRef<(() => void) | null>(null)

  // Whenever the hook becomes disabled (e.g. mode transitions out of
  // "minimized"), any in-flight gesture is invalidated — its pointerup/cancel
  // may not route back to our handler if the wrapper is hidden or removed
  // from the hit-test path. Wipe the tracking refs so the next gesture in a
  // future minimized session can start cleanly. Without this, a stale
  // pointerId could lock out future drags ("already dragging" / "pointerId
  // mismatch" early returns).
  useEffect(() => {
    if (isEnabled) return
    if (isDraggingRef.current || activePointerIdRef.current !== null) {
      dlog("isEnabled -> false while gesture active; resetting state", {
        wasDragging: isDraggingRef.current,
        activePointerId: activePointerIdRef.current,
      })
      isDraggingRef.current = false
      activePointerIdRef.current = null
      clickSuppressorCleanupRef.current?.()
      clickSuppressorCleanupRef.current = null
      endAvatarGesture(0)
    }
  }, [isEnabled])

  // Diagnostic: a document-level passive `pointercancel` listener so we can
  // see *every* cancel happening on the page, not just the ones reaching our
  // handler. If a cancel for our pointerId fires on a *different* element
  // before ours, that element is the one taking over the pointer.
  useEffect(() => {
    if (!DEBUG_AVATAR_DRAG || typeof document === "undefined") return
    const onDocCancel = (ev: Event) => {
      const pe = ev as PointerEvent
      if (!isDraggingRef.current) return
      if (pe.pointerId !== activePointerIdRef.current) return
      dlog("DOC pointercancel", {
        pointerId: pe.pointerId,
        targetTag: (ev.target as Element | null)?.tagName,
        eventPhase: ev.eventPhase,
      })
    }
    document.addEventListener("pointercancel", onDocCancel, { capture: true, passive: true })
    return () => {
      document.removeEventListener("pointercancel", onDocCancel, true)
    }
  }, [])

  /**
   * Calculate the snap position for a given corner based on current layout.
   * Coordinates are in screen-absolute space so the avatar can use the full
   * viewport width regardless of the constrained chat column.
   */
  const getCornerPosition = useCallback(
    (corner: SnapCorner): DragPosition => {
      const isLeft = corner === "top-left" || corner === "bottom-left"
      const isTop = corner === "top-left" || corner === "top-right"

      // Snap to screen edges (not the constrained canvas column)
      const leftSnapX = MINIMIZED_MARGIN
      const rightSnapX = layout.screenWidth - minimizedWidth - MINIMIZED_MARGIN
      const topSnapY = layout.canvasTop + TOP_PADDING + topClearance
      const bottomSnapY = layout.canvasBottom - minimizedHeight - MINIMIZED_MARGIN - BOTTOM_PADDING

      const x = isLeft ? leftSnapX : rightSnapX
      const y = isTop ? topSnapY : bottomSnapY

      return { x, y }
    },
    [
      layout.screenWidth,
      layout.canvasTop,
      layout.canvasBottom,
      minimizedWidth,
      minimizedHeight,
      topClearance,
    ],
  )

  /**
   * Snap to the nearest corner based on current position
   */
  const snapToNearestCorner = useCallback(
    (currentX: number, currentY: number) => {
      const corners: SnapCorner[] = ["top-left", "top-right", "bottom-left", "bottom-right"]

      const newCorner = corners.reduce((closestCorner, candidateCorner) => {
        const closestPos = getCornerPosition(closestCorner)
        const candidatePos = getCornerPosition(candidateCorner)

        const closestDistance = Math.hypot(currentX - closestPos.x, currentY - closestPos.y)
        const candidateDistance = Math.hypot(currentX - candidatePos.x, currentY - candidatePos.y)

        return candidateDistance < closestDistance ? candidateCorner : closestCorner
      }, corners[0])

      setSnapCorner(newCorner)
      const pos = getCornerPosition(newCorner)
      setWebPosition(pos)
    },
    [setSnapCorner, getCornerPosition],
  )

  /**
   * Initialize position when corner or layout changes
   */
  const initializePosition = useCallback(() => {
    if (!isEnabled) {
      setWebPosition(null)
      return
    }

    const pos = getCornerPosition(snapCorner)
    setWebPosition(pos)
  }, [isEnabled, snapCorner, getCornerPosition])

  /**
   * Swallow the next `click` (and the `touchend` that precedes it on touch
   * input) at the document capture phase, so the synthetic click that the
   * browser fires after a pointer interaction can't fall through to a sibling
   * layer below the avatar (e.g. the play/pause Pressable in
   * MediaPlayerShell). The avatar transitions out of minimized mode on tap,
   * which makes the avatar wrapper transparent to clicks at the moment the
   * synthetic click is hit-tested — this guard is required regardless of any
   * `preventDefault()` we already do on `pointerdown`, because compatibility
   * mouse/click suppression via `pointerdown.preventDefault()` is not
   * universally honored by browsers (notably some mobile/WebKit builds).
   *
   * Returns a cleanup function so callers can detach early on cancel.
   */
  const armNextClickSuppressor = useCallback(() => {
    if (typeof document === "undefined") return () => {}

    const swallowClick = (clickEvent: Event) => {
      clickEvent.stopImmediatePropagation()
      if (clickEvent.cancelable) clickEvent.preventDefault()
    }
    // `touchend.preventDefault()` is the canonical way to suppress the
    // compatibility `click` that follows a touch. It's complementary to the
    // click listener: on devices/browsers where the click capture listener
    // somehow fires after `onClick` handlers (e.g. bugs around React's event
    // delegation or pointer-event ordering), preventing the click at the
    // touch source is a more reliable backstop.
    //
    // Guard with `cancelable` because Chrome marks `touchend` as
    // `cancelable: false` once the browser has already committed to a
    // scroll/gesture; calling `preventDefault()` then is a no-op AND emits a
    // noisy "[Intervention]" console warning.
    const swallowTouchEnd = (touchEvent: Event) => {
      if (touchEvent.cancelable) touchEvent.preventDefault()
    }

    document.addEventListener("click", swallowClick, { capture: true, once: true })
    document.addEventListener("touchend", swallowTouchEnd, { capture: true, once: true })

    let cleared = false
    const clear = () => {
      if (cleared) return
      cleared = true
      document.removeEventListener("click", swallowClick, true)
      document.removeEventListener("touchend", swallowTouchEnd, true)
    }
    // Safety net: if no click/touchend fires within a short window (e.g.
    // because the gesture became a drag/throw), drop the listeners.
    setTimeout(clear, 400)
    return clear
  }, [])

  /**
   * Pointer event handlers for dragging.
   * Pointer Events provide a unified API for mouse, touch, and pen input,
   * so the same handlers work on desktop browsers and mobile/touch devices
   * (including Chrome DevTools' device emulation mode).
   */
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      dlog("pointerdown", {
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        isEnabled,
        wasDragging: isDraggingRef.current,
        activePointerId: activePointerIdRef.current,
        hasWebPosition: !!webPosition,
      })
      if (!isEnabled) {
        dlog("pointerdown -> EARLY RETURN: disabled")
        return
      }

      // Stale-state recovery: if we *think* we're dragging but a fresh
      // pointerdown arrives with a different pointer id, the tracked gesture
      // is dead — its pointerup/cancel never reached us (e.g. because the
      // wrapper was hidden during a mode transition, or another listener
      // consumed it). Without this reset, every subsequent gesture would be
      // permanently locked out: pointerdown bails ("already dragging") and
      // pointerup/cancel bails ("pointerId mismatch"). Force-reset so the
      // new gesture can start cleanly.
      if (isDraggingRef.current && activePointerIdRef.current !== e.pointerId) {
        dlog("pointerdown: force-resetting stale gesture", {
          stalePointerId: activePointerIdRef.current,
          newPointerId: e.pointerId,
          staleAgeMs: Date.now() - dragStartTime.current,
        })
        isDraggingRef.current = false
        activePointerIdRef.current = null
        clickSuppressorCleanupRef.current?.()
        clickSuppressorCleanupRef.current = null
        endAvatarGesture(0)
        // Fall through and start the new gesture below.
      } else if (isDraggingRef.current) {
        // Same pointer id (extremely rare — a duplicate pointerdown for the
        // same finger). Truly ignore secondary touches.
        dlog("pointerdown -> EARLY RETURN: already dragging (same pointerId)")
        return
      }

      isDraggingRef.current = true
      hasMovedRef.current = false
      dragStartTime.current = Date.now()
      activePointerIdRef.current = e.pointerId

      // Store absolute pointer position
      dragStartPos.current = { x: e.clientX, y: e.clientY }

      const currentPos = webPosition || getCornerPosition(snapCorner)
      dragStartAvatarPos.current = currentPos

      // Capture the pointer so we keep getting move/up events even if the
      // finger or cursor leaves the avatar bounds during the drag.
      const targetEl = e.currentTarget as Element
      let captureOk = true
      try {
        targetEl.setPointerCapture?.(e.pointerId)
      } catch (err) {
        captureOk = false
        dlog("pointerdown setPointerCapture threw", err)
        // Older browsers may not support pointer capture; safe to ignore.
      }
      // Diagnostic: detect *why* a drag terminates by listening for the
      // browser-level `lostpointercapture` event. If this fires before our
      // pointercancel handler does, capture was yanked (DOM remount, another
      // element calling setPointerCapture, etc).
      const onLost = (lostEvent: Event) => {
        const pe = lostEvent as PointerEvent
        dlog("lostpointercapture", {
          pointerId: pe.pointerId,
          targetTag: (lostEvent.target as Element | null)?.tagName,
          stillSameNode: lostEvent.target === targetEl,
          isConnected: targetEl.isConnected,
        })
      }
      targetEl.addEventListener("lostpointercapture", onLost, { once: true })
      // Diagnostic: the most common cause of an immediate `pointercancel`
      // (no `pointermove` between down and cancel) is the browser deciding
      // it owns the pointer for a native scroll/zoom gesture — which only
      // happens when `touch-action` on the touched element (or an ancestor)
      // permits it. Walk up the ancestor chain and log the first non-`auto`
      // touch-action we find. If `touch-action: none` is not actually on the
      // wrapper, we expect to see `auto` here.
      try {
        const ta: string[] = []
        let n: Element | null = targetEl
        while (n && ta.length < 8) {
          const computed = window.getComputedStyle(n).touchAction
          const id = n.id ? `#${n.id}` : ""
          const cls =
            typeof n.className === "string" && n.className
              ? `.${n.className.split(/\s+/).slice(0, 1).join(".")}`
              : ""
          ta.push(`${n.tagName}${id}${cls}=${computed}`)
          n = n.parentElement
        }
        // Stringify so values appear inline in the console without expansion.
        dlog("pointerdown touch-action chain:", ta.join(" | "))
      } catch {
        // ignore — diagnostics-only path
      }
      dlog("pointerdown OK", {
        startPos: dragStartPos.current,
        captureOk,
        targetTag: targetEl.tagName,
      })

      // Arm the click/touchend suppressor at pointerdown — the earliest
      // possible point — so the synthetic click that follows the gesture is
      // intercepted regardless of how soon after `pointerup` the browser
      // dispatches it. (Registering inside `pointerup` was racy on some
      // mobile/WebKit builds.)
      clickSuppressorCleanupRef.current?.()
      clickSuppressorCleanupRef.current = armNextClickSuppressor()

      // Mark the gesture active in a module-level guard. Sibling tap-capture
      // handlers (e.g. MediaPlayerShell's tap-to-play Pressable) consult this
      // flag and bail out, so even if the synthetic click escapes our
      // capture-phase suppressor, the guard prevents the unintended action.
      startAvatarGesture()

      e.stopPropagation()
      e.preventDefault()
    },
    [isEnabled, webPosition, snapCorner, getCornerPosition, armNextClickSuppressor],
  )

  // Trace counter to keep the noisy `pointermove` log readable — only print
  // every Nth move event when DEBUG_AVATAR_DRAG is on.
  const moveLogCounterRef = useRef(0)

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current || !isEnabled) {
        if (DEBUG_AVATAR_DRAG && !isDraggingRef.current) {
          // This case is the smoking gun for "drag broken": the user is
          // moving but we never registered a pointerdown.
          dlog("pointermove DROPPED: not dragging", {
            isEnabled,
            pointerId: e.pointerId,
          })
        }
        return
      }
      if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) {
        return
      }

      const deltaX = e.clientX - dragStartPos.current.x
      const deltaY = e.clientY - dragStartPos.current.y

      // Track if moved more than threshold for tap detection
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasMovedRef.current = true
      }

      moveLogCounterRef.current = (moveLogCounterRef.current + 1) % 8
      if (moveLogCounterRef.current === 0) {
        dlog("pointermove", { dx: Math.round(deltaX), dy: Math.round(deltaY) })
      }

      // Always update position to follow the pointer smoothly
      setWebPosition({
        x: dragStartAvatarPos.current.x + deltaX,
        y: dragStartAvatarPos.current.y + deltaY,
      })

      e.preventDefault()
    },
    [isEnabled],
  )

  const releasePointer = useCallback((e: React.PointerEvent) => {
    try {
      ;(e.currentTarget as Element).releasePointerCapture?.(e.pointerId)
    } catch {
      // ignore
    }
  }, [])

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      dlog("pointerup", {
        pointerId: e.pointerId,
        wasDragging: isDraggingRef.current,
        hasMoved: hasMovedRef.current,
        durationMs: Date.now() - dragStartTime.current,
        isEnabled,
      })
      // Only check `isDraggingRef` here — NOT `isEnabled`. If `isEnabled`
      // somehow flipped to `false` mid-gesture (e.g. mode changed), we still
      // need to clean up the tracking state and release the gesture guard.
      // Skipping cleanup is what previously left the guard flag stuck `true`
      // and silently disabled the tap-to-toggle Pressable.
      if (!isDraggingRef.current) {
        dlog("pointerup -> EARLY RETURN: not dragging")
        return
      }
      if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) {
        dlog("pointerup -> EARLY RETURN: pointerId mismatch")
        return
      }

      isDraggingRef.current = false
      activePointerIdRef.current = null
      releasePointer(e)
      e.stopPropagation()

      // Note: the click/touchend suppressor was already armed in
      // `handlePointerDown`. It auto-detaches when the synthetic click fires
      // (or after a short timeout if it never does, e.g. on drag/throw).
      // Keep the gesture-guard flag active for a short grace window so the
      // synthetic click that follows pointerup can be filtered out by any
      // tap-capture sibling.
      endAvatarGesture()

      // If the gesture is no longer enabled (mode changed during the gesture),
      // we've cleaned up the tracking state above — but we don't want to fire
      // the action logic (onExpand / onDismissToTab / snap), which assumes
      // we're still in minimized mode.
      if (!isEnabled) return

      // Check if it was a tap (no movement, quick)
      const duration = Date.now() - dragStartTime.current
      if (!hasMovedRef.current && duration < 300) {
        onExpand()
        return
      }

      // Check for throw gesture
      const currentPos = webPosition || getCornerPosition(snapCorner)
      const { canvasTop, canvasBottom } = layout

      // Middle ground: require partial off-screen release to dismiss,
      // while still allowing intentional flicks toward an edge.
      const dismissMarginX = minimizedWidth * 0.35
      const dismissMarginY = minimizedHeight * 0.35

      const isThrownOffLeft = currentPos.x < -dismissMarginX
      const isThrownOffRight = currentPos.x > layout.screenWidth - minimizedWidth + dismissMarginX
      const isThrownOffTop = currentPos.y < canvasTop - dismissMarginY
      const isThrownOffBottom = currentPos.y > canvasBottom - minimizedHeight + dismissMarginY

      if (isThrownOffLeft || isThrownOffRight || isThrownOffTop || isThrownOffBottom) {
        onDismissToTab()
        return
      }

      // Snap to nearest corner
      snapToNearestCorner(currentPos.x, currentPos.y)
    },
    [
      isEnabled,
      webPosition,
      snapCorner,
      getCornerPosition,
      layout,
      minimizedWidth,
      minimizedHeight,
      onExpand,
      onDismissToTab,
      snapToNearestCorner,
      releasePointer,
    ],
  )

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent) => {
      const targetEl = e.currentTarget as Element | null
      dlog("pointercancel", {
        pointerId: e.pointerId,
        wasDragging: isDraggingRef.current,
        targetTag: (e.target as Element | null)?.tagName,
        currentTargetTag: targetEl?.tagName,
        targetMatchesCurrent: e.target === e.currentTarget,
        targetIsConnected: targetEl?.isConnected,
        durationMs: Date.now() - dragStartTime.current,
      })
      // Same reasoning as `handlePointerUp`: only check `isDraggingRef`. We
      // must always release the guard flag if we previously armed it.
      if (!isDraggingRef.current) {
        dlog("pointercancel -> EARLY RETURN: not dragging")
        return
      }
      if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) {
        dlog("pointercancel -> EARLY RETURN: pointerId mismatch")
        return
      }

      isDraggingRef.current = false
      activePointerIdRef.current = null
      releasePointer(e)

      // Cancelled gestures don't generate a synthetic click — drop the
      // suppressor early so it can't swallow an unrelated click later.
      clickSuppressorCleanupRef.current?.()
      clickSuppressorCleanupRef.current = null

      // Likewise, release the gesture guard immediately on cancel (no
      // synthetic click is coming, so no grace window is needed).
      endAvatarGesture(0)

      if (!isEnabled) return

      // Use the current webPosition state which reflects the dragged position
      const currentPos = webPosition || getCornerPosition(snapCorner)
      snapToNearestCorner(currentPos.x, currentPos.y)
    },
    [isEnabled, webPosition, snapCorner, getCornerPosition, snapToNearestCorner, releasePointer],
  )

  return {
    webPosition,
    initializePosition,
    /**
     * Pointer-event handlers to spread onto the draggable element. Works for
     * both mouse and touch input on web. Pair with `touchAction: "none"` on
     * the same element to prevent the browser from scrolling/zooming during
     * the drag.
     */
    dragHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel,
    },
  }
}
